import { type } from 'arktype';

/**
 * Booking categories that the availability endpoint understands as a closed
 * vocabulary. Aligned with the Globetrotter demo `BookingCategory` enum.
 */
export const AVAILABILITY_BOOKING_CATEGORIES = ['Video call', 'Phone call', 'On-site'] as const;
export type AvailabilityBookingCategory = (typeof AVAILABILITY_BOOKING_CATEGORIES)[number];

/**
 * `needs` selector vocabulary. A slot must overlap every requested need.
 *
 * - `wlp` — slot must fall inside the person's `WeeklyLocationPattern` entries
 * - `shift.<customPropertyKey>` — slot must overlap a shift assignment whose
 *   `shift.data.customProperties[<key>]` is truthy
 * - `shift.<customPropertyKey>:<value>` — equality match on the custom value
 */
export interface AvailabilityNeed {
  kind: 'wlp' | 'shift';
  /** Custom-property key when `kind === 'shift'`. */
  customPropertyKey?: string;
  /** Optional equality match on the custom-property value (string form). */
  customPropertyValue?: string;
}

/**
 * `blockers` selector vocabulary. Slots overlapping any blocker are removed.
 */
export const AVAILABILITY_BLOCKERS = ['absences', 'meetings', 'booked-slots', 'holidays', 'team-meetings', 'shifts.non-bookable'] as const;

export type AvailabilityBlocker = (typeof AVAILABILITY_BLOCKERS)[number];

export const DEFAULT_AVAILABILITY_BLOCKERS: readonly AvailabilityBlocker[] = [
  'absences',
  'meetings',
  'booked-slots',
  'holidays',
  'team-meetings',
];

/**
 * Optional response enrichments requested via `include`.
 */
export const AVAILABILITY_INCLUDE_VALUES = ['shift', 'location', 'categories'] as const;
export type AvailabilityIncludeOption = (typeof AVAILABILITY_INCLUDE_VALUES)[number];

/**
 * Optional response exclusions requested via `exclude`.
 *
 * - `slots` — omit slot details from each date entry; only dates with at least
 *   one bookable slot are returned. Use for lightweight date-availability checks
 *   (e.g. 90-day range queries to populate calendar date pills).
 */
export const AVAILABILITY_EXCLUDE_VALUES = ['slots'] as const;
export type AvailabilityExcludeOption = (typeof AVAILABILITY_EXCLUDE_VALUES)[number];

/**
 * Parsed and normalised availability request.
 */
export interface AvailabilityQuery {
  personIds: string[];
  from: string;
  to: string;
  slotStepMinutes: number;
  needs: AvailabilityNeed[];
  blockers: AvailabilityBlocker[];
  include: AvailabilityIncludeOption[];
  exclude: AvailabilityExcludeOption[];
}

/**
 * Raw arktype schema of the inbound query string. Strings only — the
 * controller normalises into {@link AvailabilityQuery} via {@link parseAvailabilityQuery}.
 */
export const availabilityQuerySchema = type({
  personIds: type('string').configure({
    description: 'Comma-separated list of person UUIDs (1-50).',
    examples: ['p1,p2,p3'],
  }),
  from: type('string').configure({
    description: 'Inclusive lower bound of the query range (YYYY-MM-DD).',
    examples: ['2026-04-13'],
  }),
  to: type('string').configure({
    description: 'Inclusive upper bound of the query range (YYYY-MM-DD). Range capped at 92 days.',
    examples: ['2026-05-13'],
  }),
  'slotStepMinutes?': type('string').configure({
    description: 'Granularity of returned slots in minutes. Defaults to 15. Must divide 60.',
    examples: ['15'],
  }),
  'needs?': type('string').configure({
    description: 'Comma-separated need selectors. Defaults to `wlp`. AND-ed together.',
    examples: ['shift.bookable'],
  }),
  'blockers?': type('string').configure({
    description:
      'Comma-separated blocker selectors. Defaults to ' +
      '`absences,meetings,booked-slots,holidays,team-meetings`. Set to an empty string to disable all blockers.',
    examples: ['absences,meetings'],
  }),
  'include?': type('string').configure({
    description: 'Comma-separated response enrichments. Supported values: `shift`, `location`, `categories`.',
    examples: ['shift,categories'],
  }),
  'exclude?': type('string').configure({
    description:
      'Comma-separated response exclusions. Supported values: `slots`. When `slots` is specified, slot details are omitted and only dates with at least one bookable slot are returned.',
    examples: ['slots'],
  }),
}).configure({ description: 'Query parameters for the calendar availability endpoint.' });

export type AvailabilityQueryRaw = typeof availabilityQuerySchema.t;

/**
 * Shape of a single resolved availability slot.
 */
export interface AvailabilitySlot {
  /** HH:mm 24-hour clock. */
  startTime: string;
  /** HH:mm 24-hour clock; exclusive upper bound. */
  endTime: string;
  /** Resolved location for this slot — drives meeting-type derivation. */
  locationId: string | null;
  /** Present when `include` contains `shift`. */
  shift?: {
    id: string;
    assignmentId: string | null;
    customProperties: Record<string, unknown>;
  };
  /** Present when `include` contains `categories`. */
  categories?: AvailabilityBookingCategory[];
}

export interface AvailabilityDate {
  date: string;
  slots: AvailabilitySlot[];
}

export interface AvailabilityPerson {
  personId: string;
  dates: AvailabilityDate[];
}

export interface AvailabilityResponse {
  data: AvailabilityPerson[];
  meta: {
    from: string;
    to: string;
    slotStepMinutes: number;
    needs: string[];
    blockers: AvailabilityBlocker[];
    totalSlots: number;
  };
}

/** Hard caps to keep payloads sane. Used as seed/fallback values. */
export const AVAILABILITY_LIMITS = {
  MAX_PERSON_IDS: 50,
  MAX_RANGE_DAYS: 92,
  DEFAULT_SLOT_STEP_MINUTES: 15,
  ALLOWED_SLOT_STEP_DIVISORS_OF: 60,
} as const;

/** DB-configurable form of {@link AVAILABILITY_LIMITS}. Stored under key `calendar.availabilityLimits`. */
export interface AvailabilityLimitsConfig {
  maxPersonIds: number;
  maxRangeDays: number;
  defaultSlotStepMinutes: number;
  allowedSlotStepDivisorsOf: number;
}

const DEFAULT_LIMITS: AvailabilityLimitsConfig = {
  maxPersonIds: AVAILABILITY_LIMITS.MAX_PERSON_IDS,
  maxRangeDays: AVAILABILITY_LIMITS.MAX_RANGE_DAYS,
  defaultSlotStepMinutes: AVAILABILITY_LIMITS.DEFAULT_SLOT_STEP_MINUTES,
  allowedSlotStepDivisorsOf: AVAILABILITY_LIMITS.ALLOWED_SLOT_STEP_DIVISORS_OF,
};

/**
 * Parse and validate a raw query object into an {@link AvailabilityQuery}.
 *
 * Throws a structured {@link AvailabilityQueryError} when validation fails so
 * the controller can translate it to an HTTP exception. Pure function — no I/O.
 */
export function parseAvailabilityQuery(raw: AvailabilityQueryRaw, limits: AvailabilityLimitsConfig = DEFAULT_LIMITS): AvailabilityQuery {
  const personIds = splitCsv(raw.personIds)
    .map(s => s.trim())
    .filter(Boolean);
  if (personIds.length === 0 || personIds.length > limits.maxPersonIds) {
    throw new AvailabilityQueryError(`personIds must contain between 1 and ${limits.maxPersonIds} entries`);
  }

  if (!isIsoDate(raw.from)) throw new AvailabilityQueryError('`from` must be a YYYY-MM-DD date');
  if (!isIsoDate(raw.to)) throw new AvailabilityQueryError('`to` must be a YYYY-MM-DD date');
  if (raw.from > raw.to) throw new AvailabilityQueryError('`from` must be on or before `to`');
  if (daysBetween(raw.from, raw.to) > limits.maxRangeDays) {
    throw new AvailabilityQueryError(`Date range exceeds the ${limits.maxRangeDays}-day cap`);
  }

  const slotStepMinutes = parseSlotStep(raw.slotStepMinutes, limits);
  const needs = parseNeeds(raw.needs);
  const blockers = parseBlockers(raw.blockers);
  const include = parseInclude(raw.include);
  const exclude = parseExclude(raw.exclude);

  return { personIds, from: raw.from, to: raw.to, slotStepMinutes, needs, blockers, include, exclude };
}

export class AvailabilityQueryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AvailabilityQueryError';
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function splitCsv(input: string | undefined): string[] {
  return typeof input === 'string' ? input.split(',') : [];
}

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000) + 1;
}

function parseSlotStep(raw: string | undefined, limits: AvailabilityLimitsConfig): number {
  if (raw === undefined || raw === '') return limits.defaultSlotStepMinutes;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 5 || value > 60) {
    throw new AvailabilityQueryError('slotStepMinutes must be an integer between 5 and 60');
  }
  if (limits.allowedSlotStepDivisorsOf % value !== 0) {
    throw new AvailabilityQueryError('slotStepMinutes must divide 60');
  }
  return value;
}

function parseNeeds(raw: string | undefined): AvailabilityNeed[] {
  const tokens = splitCsv(raw)
    .map(s => s.trim())
    .filter(Boolean);
  if (tokens.length === 0) return [{ kind: 'wlp' }];
  return tokens.map(parseNeedToken);
}

function parseNeedToken(token: string): AvailabilityNeed {
  if (token === 'wlp') return { kind: 'wlp' };
  if (token.startsWith('shift.')) {
    const rest = token.slice('shift.'.length);
    const [key, value] = rest.split(':', 2);
    if (!key) throw new AvailabilityQueryError(`Invalid need selector: ${token}`);
    return { kind: 'shift', customPropertyKey: key, customPropertyValue: value };
  }
  throw new AvailabilityQueryError(`Unsupported need selector: ${token}`);
}

function parseBlockers(raw: string | undefined): AvailabilityBlocker[] {
  if (raw === undefined) return [...DEFAULT_AVAILABILITY_BLOCKERS];
  if (raw === '') return [];
  const tokens = splitCsv(raw)
    .map(s => s.trim())
    .filter(Boolean);
  for (const t of tokens) {
    if (!AVAILABILITY_BLOCKERS.includes(t as AvailabilityBlocker)) {
      throw new AvailabilityQueryError(`Unsupported blocker: ${t}`);
    }
  }
  return tokens as AvailabilityBlocker[];
}

function parseInclude(raw: string | undefined): AvailabilityIncludeOption[] {
  if (raw === undefined || raw === '') return [];
  const tokens = splitCsv(raw)
    .map(s => s.trim())
    .filter(Boolean);
  for (const t of tokens) {
    if (!AVAILABILITY_INCLUDE_VALUES.includes(t as AvailabilityIncludeOption)) {
      throw new AvailabilityQueryError(`Unsupported include value: ${t}`);
    }
  }
  return tokens as AvailabilityIncludeOption[];
}

function parseExclude(raw: string | undefined): AvailabilityExcludeOption[] {
  if (raw === undefined || raw === '') return [];
  const tokens = splitCsv(raw)
    .map(s => s.trim())
    .filter(Boolean);
  for (const t of tokens) {
    if (!AVAILABILITY_EXCLUDE_VALUES.includes(t as AvailabilityExcludeOption)) {
      throw new AvailabilityQueryError(`Unsupported exclude value: ${t}`);
    }
  }
  return tokens as AvailabilityExcludeOption[];
}
